import 'react-international-phone/style.css';

import * as yup from 'yup';

import { Add as AddIcon, ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { Alert, Avatar, Box, Button, Container, FormControl, IconButton, InputLabel, MenuItem, Select, TextField, Typography } from '@mui/material';
import { Controller, useForm, useWatch } from 'react-hook-form';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import MuiAutoCompleteCountry from "../../components/InputControls/MuiAutoCompleteCountry";
import { PhoneInput } from 'react-international-phone';
import api from "../../api/axiosInstance";
import { formatDateForInput } from "../../components/utils/dateUtils";
import { useNavigate } from 'react-router-dom';
import { useProfileContext } from "../../providers/ProfileProvider";
import { yupResolver } from '@hookform/resolvers/yup';

const VALIDATION_PATTERNS = {
  PHONE: /^\+?[1-9]\d{1,14}$/, 
  EMAIL: /^[^\s@]+@[^\s@]+\.[^\s@]+$/
};

const GENDERS = ['Male', 'Female', 'Other'];

const FORM_DEFAULTS = {
  firstName: '',
  lastName: '',
  primaryEmail: '',
  secondaryEmail: '',
  phoneNumber: '',
  gender: '',
  dateOfBirth: '',
  country: null
};

const COUNTRY_FIELD_STYLES = {
  '& .MuiAutocomplete-root': {
    '& .MuiOutlinedInput-root': {
      minHeight: '40px !important',
      height: '40px !important',
      '& .MuiAutocomplete-input': {
        padding: '8.5px 14px !important',
      }
    },
    '& .MuiInputLabel-root': {
      transform: 'translate(14px, 9px) scale(1)',
      '&.MuiInputLabel-shrink': {
        transform: 'translate(14px, -9px) scale(0.75)',
      }
    }
  },
  '& input': {
    height: '40px !important',
    boxSizing: 'border-box',
  },
  '& .MuiInputBase-root': {
    height: '40px !important',
  }
};

const personalDetailsSchema = yup.object().shape({
  firstName: yup.string().required('First name is required'),
  lastName: yup.string().required('Last name is required'),
  primaryEmail: yup.string(),
  secondaryEmail: yup
    .string()
    .matches(VALIDATION_PATTERNS.EMAIL, 'Please enter a valid email address')
    .required('Secondary email is required'),
  phoneNumber: yup
    .string()
    .required('Phone number is required')
    .min(10, 'Phone number must be at least 10 digits'), 
  gender: yup.string().required('Gender is required'),
  dateOfBirth: yup
    .string()
    .test(
      'age-validation',
      'You must be 18 years or older',
      function (value) {
        if (!value) return false;
        const today = new Date();
        const birthDate = new Date(value);
        const age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();
        
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
          return age - 1 >= 18;
        }
        return age >= 18;
      }
    )
    .required('Date of birth is required'),
  country: yup
    .object()
    .shape({
      code: yup.string().required(),
      label: yup.string().required(),
    })
    .nullable()
    .required("Country is required")
    .default(null)
});

const usePersonalDetailsForm = (initialData) => {
  return useForm({
    resolver: yupResolver(personalDetailsSchema),
    mode: 'onChange',
    defaultValues: initialData
  });
};

const apiService = {
  savePersonalDetails: async (data) => {
    const serverUrl = process.env.REACT_APP_SERVER_URL;

    if (!serverUrl) {
      throw new Error('API configuration is missing. Please contact support.');
    }

    const body = {
      secondaryEmail: data.secondaryEmail || undefined,
      phoneNumber: data.phoneNumber || undefined,
      gender: data.gender || undefined,
      dateOfBirth: data.dateOfBirth || undefined,
      country: data.country ? JSON.stringify(data.country) : undefined,
    };

    try {
      const response = await api.patch(
        "/users/me",
        body,
        { headers: { "Content-Type": "application/json" } }
      );
      return response.data;
    } catch (error) {
      const msg = error.response?.data?.message || error.response?.data?.error || 'An unexpected error occurred. Please try again.';
      throw new Error(msg);
    }
  }
};

const FormHeader = () => (
  <Box sx={{ textAlign: 'center', mb: 3 }}>
    <Typography variant="h6" sx={{ mb: 1 }}>
      Personal Details
    </Typography>
    <Typography variant="body2">
      Let's get to know you more.
    </Typography>
  </Box>
);

const ProfilePhoto = ({ firstName, lastName }) => {
  const initials = useMemo(() => {
    if (firstName && lastName) {
      return `${firstName[0]}${lastName[0]}`.toUpperCase();
    }
    return 'OP';
  }, [firstName, lastName]);

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
      <Box sx={{ position: 'relative' }}>
        <Avatar 
          alt="Avatar" 
          sx={{ 
            width: 96, 
            height: 96, 
            bgcolor: 'grey.400',
            fontSize: '32px',
            fontWeight: 'bold'
          }}
        >
          {initials}
        </Avatar>
        <IconButton
          sx={{
            position: 'absolute',
            bottom: 5,
            right: 5,
            bgcolor: 'white',
            border: 1,
            borderColor: 'grey.300',
            width: 32,
            height: 32,
            boxShadow: 2,
            '&:hover': {
              bgcolor: 'grey.50',
              transform: 'scale(1.05)'
            }
          }}
        >
          <AddIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>
    </Box>
  );
};

const NameFields = ({ control, errors }) => (
  <Box sx={{ display: 'flex', gap: 2 }}>
    <Controller
      name="firstName"
      control={control}
      render={({ field }) => (
        <TextField
          {...field}
          fullWidth
          label="First Name"
          placeholder="Jane"
          required
          size="small"
          disabled
          error={!!errors.firstName}
          helperText={errors.firstName?.message}
          sx={{
            '& .MuiInputBase-input.Mui-disabled': {
              WebkitTextFillColor: 'rgba(0, 0, 0, 0.6)',
              backgroundColor: '#f5f5f5'
            }
          }}
        />
      )}
    />
    <Controller
      name="lastName"
      control={control}
      render={({ field }) => (
        <TextField
          {...field}
          fullWidth
          label="Last Name"
          placeholder="Doe"
          required
          size="small"
          disabled
          error={!!errors.lastName}
          helperText={errors.lastName?.message}
          sx={{
            '& .MuiInputBase-input.Mui-disabled': {
              WebkitTextFillColor: 'rgba(0, 0, 0, 0.6)',
              backgroundColor: '#f5f5f5'
            }
          }}
        />
      )}
    />
  </Box>
);

const EmailFields = ({ control, errors }) => {
  const primaryEmail = useWatch({ control, name: 'primaryEmail' });
  const secondaryEmail = useWatch({ control, name: 'secondaryEmail' });
  const showSameEmailWarning = primaryEmail && primaryEmail === secondaryEmail;

  return (
    <>
      <Controller
        name="primaryEmail"
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            fullWidth
            label="Primary Email"
            type="email"
            placeholder="example@gmail.com"
            size="small"
            disabled
            sx={{
              '& .MuiInputBase-input.Mui-disabled': {
                WebkitTextFillColor: 'rgba(0, 0, 0, 0.6)',
                backgroundColor: '#f5f5f5'
              }
            }}
          />
        )}
      />
      <Controller
        name="secondaryEmail"
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            fullWidth
            label="Secondary Email"
            type="email"
            placeholder="example@gmail.com"
            size="small"
            required
            error={!!errors.secondaryEmail}
            helperText={errors.secondaryEmail?.message}
            autoComplete='off'
          />
        )}
      />
      {showSameEmailWarning && (
        <Typography variant="sm" color="warning.main">
          ⚠️ Using a different email address as your secondary contact improves account security and makes recovery easier if you lose access to your primary email.
        </Typography>
      )}
    </>
  );
};

const PhoneNumberField = ({ control, errors }) => {
  return (
    <Box sx={{ position: 'relative', zIndex: 10 }}>
      <Typography variant="fieldLabel">
        Phone <Box component="span" sx={{ color: 'error.main' }}>*</Box>
      </Typography>
      <Controller
        name="phoneNumber"
        control={control}
        defaultValue=""
        render={({ field: { onChange, value } }) => (
          <PhoneInput
            value={value || ''}
            onChange={(phone) => {
              if (!phone || phone.length <= 4) {
                return;
              }
              onChange(phone);
            }}
            defaultCountry="ke"
            style={{
              width: '100%',
            }}
            inputStyle={{
              width: '100%',
              height: '40px',
              fontSize: '16px',
              borderColor: errors.phoneNumber ? '#d32f2f' : undefined,
            }}
            countrySelectorStyleProps={{
              buttonStyle: {
                height: '40px',
                paddingLeft: '12px',
                paddingRight: '8px',
              },
              dropdownStyleProps: {
                style: {
                  top: '42px',
                },
                // Add this to enable full name search
                searchStyle: {
                  marginBottom: '8px',
                },
              }
            }}
            enableSearch={true}
            searchNotFound="No country found"
            forceDialCode={true}
          />
        )}
      />
      {errors.phoneNumber && (
        <Typography 
          variant="caption" 
          color="error" 
          sx={{ ml: 2, mt: 0.5, display: 'block' }}
        >
          {errors.phoneNumber.message}
        </Typography>
      )}
    </Box>
  );
};

const GenderAndDateFields = ({ control, errors }) => {
  const getCurrentDate = () => new Date().toISOString().split('T')[0];
  const getMinDate = () => {
    const today = new Date();
    const minDate = new Date(today.getFullYear() - 100, today.getMonth(), today.getDate());
    return minDate.toISOString().split('T')[0];
  };

  return (
    <Box sx={{ display: 'flex', gap: 2 }}>
      <Controller
        name="gender"
        control={control}
        render={({ field }) => (
          <FormControl fullWidth size="small" error={!!errors.gender}>
            <InputLabel shrink required>Gender</InputLabel>
            <Select
              {...field}
              label="Gender *"
              value={field.value || ''}
              displayEmpty
              notched
            >
              <MenuItem value=""><em>Select</em></MenuItem>
              {GENDERS.map((gender) => (
                <MenuItem key={gender} value={gender}>{gender}</MenuItem>
              ))}
            </Select>
            {errors.gender && (
              <Typography variant="caption" color="error" sx={{ ml: 2, mt: 0.5 }}>
                {errors.gender.message}
              </Typography>
            )}
          </FormControl>
        )}
      />

      <Controller
        name="dateOfBirth"
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            fullWidth
            label="Date of Birth"
            type="date"
            size="small"
            required
            error={!!errors.dateOfBirth}
            helperText={errors.dateOfBirth?.message}
            InputLabelProps={{ shrink: true }}
            inputProps={{
              max: getCurrentDate(), 
              min: getMinDate(),
            }}
          />
        )}
      />
    </Box>
  );
};

const CountryField = ({ control, errors }) => (
  <Box sx={COUNTRY_FIELD_STYLES}>
    <MuiAutoCompleteCountry control={control} errors={errors} />
  </Box>
);

const FormActions = ({ onBack, isValid, isSubmitting, isFirstStep }) => (
  <Box sx={{ 
    display: 'flex', 
    justifyContent: isFirstStep ? 'flex-end' : 'space-between', 
    mt: 2
  }}>
    {!isFirstStep && (
      <Button
        variant="outlined"
        startIcon={<ArrowBackIcon />}
        onClick={onBack}
        disabled={isSubmitting}
        size="small"
      >
        Back
      </Button>
    )}

    <Button
      type="submit"
      variant="contained"
      disabled={!isValid || isSubmitting}
      size="small"
      sx={{ px: 3 }}
    >
      {isSubmitting ? 'Saving...' : 'Next'}
    </Button>
  </Box>
);

const StatusAlert = ({ saveStatus, onClose }) => {
  if (!saveStatus) return null;

  return (
    <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
      <Alert 
        severity={saveStatus.type} 
        sx={{ width: '512px' }}
        onClose={onClose}
        action={
          saveStatus.type === 'error' && saveStatus.retry ? (
            <Button color="inherit" size="small" onClick={saveStatus.retry}>
              Retry
            </Button>
          ) : null
        }
      >
        {saveStatus.message}
      </Alert>
    </Box>
  );
};

const PersonalDetailsForm = ({
  formData,
  setFormData,
  onNext,
  onBack,
  isFirstStep = false
}) => {
  const navigate = useNavigate();
  const { profile, refreshProfile } = useProfileContext();
  const [saveStatus, setSaveStatus] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [initialData, setInitialData] = useState(FORM_DEFAULTS);
  const [profileLoaded, setProfileLoaded] = useState(false);
  
  // Load profile data ONCE when component mounts
  useEffect(() => {
    if (!profile || profileLoaded) return;
    
    if (typeof profile === 'string' || !profile.email) {
      console.error('Invalid profile data received');
      return;
    }
    
    let parsedCountry = profile.country;
    if (typeof profile.country === 'string') {
      try {
        parsedCountry = JSON.parse(profile.country);
      } catch (e) {
        parsedCountry = null;
      }
    }
    
    const profileData = {
      firstName: profile.firstName || '',
      lastName: profile.lastName || '',
      primaryEmail: profile.email || '',
      secondaryEmail: profile.secondaryEmail || '',
      phoneNumber: profile.phoneNumber || '', 
      gender: profile.gender || '',
      dateOfBirth: formatDateForInput(profile.dateOfBirth),
      country: parsedCountry || null
    };
    
    setInitialData(profileData);
    setFormData(prev => ({ ...prev, ...profileData }));
    setProfileLoaded(true);
  }, [profile, setFormData, profileLoaded]);

  const { control, handleSubmit, watch, reset, formState: { errors, isValid } } = usePersonalDetailsForm(initialData);

  useEffect(() => {
    if (initialData !== FORM_DEFAULTS && profileLoaded) {
      reset(initialData);
    }
  }, [initialData, reset, profileLoaded]);

  useEffect(() => {
    if (!profileLoaded) return;
    
    const subscription = watch((watchedValues) => {
      const cleanedValues = Object.keys(watchedValues).reduce((acc, key) => {
        if (watchedValues[key] !== undefined) {
          acc[key] = watchedValues[key];
        }
        return acc;
      }, {});

      setFormData(prev => ({ ...prev, ...cleanedValues }));
    });

    return () => subscription.unsubscribe();
  }, [watch, setFormData, profileLoaded]);

  const onSubmit = useCallback(async (data) => {
    
    setIsSubmitting(true);
    setSaveStatus(null);
    
    try {
      await apiService.savePersonalDetails(data);
      setFormData(prev => ({ ...prev, ...data }));
      await refreshProfile();
      onNext();
    } catch (error) {
      if (error.message.includes('session has expired') || error.message.includes('log in again')) {
        setSaveStatus({ type: 'error', message: error.message });
        setTimeout(() => navigate('/'), 2000);
        return;
      }

      setSaveStatus({ 
        type: 'error', 
        message: error.message || 'Failed to save personal details. Please try again.',
        retry: () => {
          setSaveStatus(null);
          handleSubmit(onSubmit)();
        }
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [setFormData, onNext, navigate, handleSubmit, refreshProfile]);

  const watchedValues = watch();

  if (!profile || !profileLoaded) {
    return (
      <Box sx={{ 
        minHeight: 'calc(100vh - 116px)',
        bgcolor: '#EAE7E6',
        pt: 2,
        px: '80px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
        <Typography>Loading your profile...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      minHeight: 'calc(100vh - 116px)',
      bgcolor: '#EAE7E6',
      pt: 2,
      px: '80px',
      pb: 3
    }}>
      <Container maxWidth={false} sx={{ px: 0, height: '100%' }}>
        <Box sx={{ 
          p: 4, 
          bgcolor: 'white',
          minHeight: 'calc(100vh - 116px - 32px)',
          display: 'flex',
          flexDirection: 'column'
        }}>
          <FormHeader />
          <ProfilePhoto firstName={watchedValues.firstName} lastName={watchedValues.lastName} />
          <StatusAlert saveStatus={saveStatus} onClose={() => setSaveStatus(null)} />

          <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
            <Box sx={{ width: '512px', display: 'flex', flexDirection: 'column', gap: 2 }}>
              <form onSubmit={handleSubmit(onSubmit)} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <NameFields control={control} errors={errors} />
                  <EmailFields control={control} errors={errors} />
                  <PhoneNumberField control={control} errors={errors} />
                  <GenderAndDateFields control={control} errors={errors} />
                  <CountryField control={control} errors={errors} />
                </Box>
                <FormActions onBack={onBack} isValid={isValid} isSubmitting={isSubmitting} isFirstStep={isFirstStep} />
              </form>
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default PersonalDetailsForm;